import type { ExerciseAttempt } from '@/entities/attempt'
import type {
  GardenZone,
  PlantHealthStatus,
  PlantState,
} from '@/types/plantTypes'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export const PLANT_LIFECYCLE = Object.freeze({
  maxNutrient: 100,
  initialNutrient: 80,
  nourishmentPerQuestion: 12,
  gracePeriodMs: 48 * HOUR_MS,
  decayPerDay: 8,
  healthyThreshold: 50,
  thirstyThreshold: 20,
})

export interface PlantVitalityProjection {
  nutrient: number
  displayNutrient: number
  status: PlantHealthStatus
  daysUntilDead: number
  nextTransitionAt: string | null
  decayStartedAt: string | null
}

export interface PlantNourishmentEvent {
  eventId: string
  sourceType: 'formative_attempt'
  sourceId: string
  attemptId: string
  zone: GardenZone
  amount: number
  occurredAt: string
}

function timeValue(value: string | null | undefined): number | null {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

function clampNutrient(value: number): number {
  return Math.max(0, Math.min(PLANT_LIFECYCLE.maxNutrient, Number(value) || 0))
}

export function plantHealthStatus(nutrient: number): PlantHealthStatus {
  if (nutrient <= 0) return 'dead'
  if (nutrient < PLANT_LIFECYCLE.thirstyThreshold) return 'wilted'
  if (nutrient < PLANT_LIFECYCLE.healthyThreshold) return 'thirsty'
  return 'healthy'
}

export function projectPlantVitality(
  plant: Pick<PlantState, 'species' | 'nutrient' | 'lastNourishedAt'>,
  now: string | number | Date = Date.now(),
): PlantVitalityProjection {
  const baseNutrient = plant.species ? clampNutrient(plant.nutrient) : 0
  const lastNourishedAt = timeValue(plant.lastNourishedAt)
  const nowTime = now instanceof Date ? now.getTime() : typeof now === 'number' ? now : timeValue(now) ?? Date.now()

  if (!plant.species || baseNutrient <= 0) {
    return {
      nutrient: 0,
      displayNutrient: 0,
      status: 'dead',
      daysUntilDead: 0,
      nextTransitionAt: null,
      decayStartedAt: lastNourishedAt === null ? null : new Date(lastNourishedAt + PLANT_LIFECYCLE.gracePeriodMs).toISOString(),
    }
  }

  // Legacy plants with no nourishment timestamp begin decay only after their first new learning event.
  if (lastNourishedAt === null || nowTime <= lastNourishedAt + PLANT_LIFECYCLE.gracePeriodMs) {
    const decayStartedAt = lastNourishedAt === null
      ? null
      : new Date(lastNourishedAt + PLANT_LIFECYCLE.gracePeriodMs).toISOString()
    return {
      nutrient: baseNutrient,
      displayNutrient: Math.ceil(baseNutrient),
      status: plantHealthStatus(baseNutrient),
      daysUntilDead: PLANT_LIFECYCLE.gracePeriodMs / DAY_MS + baseNutrient / PLANT_LIFECYCLE.decayPerDay,
      nextTransitionAt: decayStartedAt,
      decayStartedAt,
    }
  }

  const decayStartedAtMs = lastNourishedAt + PLANT_LIFECYCLE.gracePeriodMs
  const decayed = ((nowTime - decayStartedAtMs) / DAY_MS) * PLANT_LIFECYCLE.decayPerDay
  const nutrient = clampNutrient(baseNutrient - decayed)
  const status = plantHealthStatus(nutrient)
  const nextThreshold = status === 'healthy'
    ? PLANT_LIFECYCLE.healthyThreshold
    : status === 'thirsty'
      ? PLANT_LIFECYCLE.thirstyThreshold
      : 0
  const nextTransitionAt = status === 'dead'
    ? null
    : new Date(decayStartedAtMs + ((baseNutrient - nextThreshold) / PLANT_LIFECYCLE.decayPerDay) * DAY_MS).toISOString()

  return {
    nutrient,
    displayNutrient: Math.ceil(nutrient),
    status,
    daysUntilDead: nutrient / PLANT_LIFECYCLE.decayPerDay,
    nextTransitionAt,
    decayStartedAt: new Date(decayStartedAtMs).toISOString(),
  }
}

export function nourishPlant(
  plant: PlantState,
  occurredAt: string,
  amount: number = PLANT_LIFECYCLE.nourishmentPerQuestion,
): { plant: PlantState; applied: boolean } {
  if (!plant.species || !plant.instanceId) return { plant, applied: false }
  const eventTime = timeValue(occurredAt)
  if (eventTime === null) return { plant, applied: false }

  const effectiveTime = Math.max(eventTime, timeValue(plant.lastNourishedAt) ?? eventTime)
  const projected = projectPlantVitality(plant, effectiveTime)
  if (projected.status === 'dead') {
    return { plant: { ...plant, nutrient: 0 }, applied: false }
  }

  return {
    applied: true,
    plant: {
      ...plant,
      nutrient: clampNutrient(projected.nutrient + Math.max(0, amount)),
      lastNourishedAt: new Date(effectiveTime).toISOString(),
    },
  }
}

export function normalizePlantState(
  plant: Partial<PlantState> | null | undefined,
  zone: GardenZone,
): PlantState {
  const exp = Math.max(0, Math.floor(Number(plant?.exp) || 0))
  const species = plant?.species ?? null
  const plantedAt = species ? plant?.plantedAt ?? null : null
  return {
    species,
    exp,
    growthExp: Math.max(0, Math.floor(Number(plant?.growthExp ?? exp) || 0)),
    stage: plant?.stage ?? 'seedling',
    plantedAt,
    instanceId: species
      ? plant?.instanceId ?? `legacy:${zone}:${plantedAt ?? 'unknown'}`
      : null,
    nutrient: species
      ? clampNutrient(plant?.nutrient ?? PLANT_LIFECYCLE.initialNutrient)
      : 0,
    lastNourishedAt: species ? plant?.lastNourishedAt ?? null : null,
  }
}

function stableHash(value: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function attemptZone(attempt: ExerciseAttempt): GardenZone | null {
  if (attempt.ability) return attempt.ability
  const vocabularySignal = [
    attempt.exerciseId,
    attempt.knowledgePointId,
    attempt.category,
    attempt.subCategory,
    attempt.sceneType,
  ].filter(Boolean).join(':').toLowerCase()
  return /(^|[:_-])(vocab|vocabulary|word)([:_-]|$)/.test(vocabularySignal)
    ? 'vocabulary'
    : null
}

export function createNourishmentEvent(attempt: ExerciseAttempt): PlantNourishmentEvent | null {
  if (attempt.evidenceKind !== 'formative') return null
  if (attempt.source === 'assessment' || attempt.source === 'ai-teacher') return null
  if (attempt.scoringMethod === 'not-observed' || attempt.scoringMethod === 'simulated') return null
  if (attempt.retryCount !== 0) return null
  if (!timeValue(attempt.completedAt)) return null
  const zone = attemptZone(attempt)
  if (!zone) return null

  const scope = attempt.stageId ?? attempt.unitId ?? attempt.lessonId ?? attempt.taskId ?? 'unscoped'
  const questionIdentity = attempt.contentFingerprint ?? attempt.exerciseId
  const naturalKey = [attempt.studentId, attempt.source, scope, questionIdentity].join('|')
  const sourceId = `question:${stableHash(naturalKey)}`
  return {
    eventId: `plant-nourish:${stableHash(`${naturalKey}|v1`)}`,
    sourceType: 'formative_attempt',
    sourceId,
    attemptId: attempt.id,
    zone,
    amount: PLANT_LIFECYCLE.nourishmentPerQuestion,
    occurredAt: attempt.completedAt,
  }
}

export function collectNourishmentEvents(attempts: readonly ExerciseAttempt[]): PlantNourishmentEvent[] {
  const events = new Map<string, PlantNourishmentEvent>()
  const chronological = [...attempts].sort((left, right) => (
    new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime()
  ))
  for (const attempt of chronological) {
    const event = createNourishmentEvent(attempt)
    if (event && !events.has(event.sourceId)) events.set(event.sourceId, event)
  }
  return [...events.values()]
}
